import AimoMarker from '@components/map/AimoMarker';
import { Clusterer } from '@react-google-maps/marker-clusterer';
import AimoPolygon from 'components/map/AimoPolygon';
import React, { useMemo } from 'react';
import { useActiveElementStore } from 'stores/activeElementStore';
import { Maybe, ParkZone } from 'types/generatedSchemaTypes';
import { MarkerTypes, PolygonItem } from 'types/MapTypes';
import { getMarkerLetter } from 'utils/cityParkingUtils';
import { isIos } from 'utils/deviceUtils';
import { isSelectedElement, makePoint } from 'utils/mapUtils';

const Empty = () => <></>;

export const MapMarkers = (
  clusterer: Clusterer | undefined,
  parkingZonesArray: Maybe<ParkZone>[] | undefined,
  onItemSelect: (parkZoneUid: Maybe<string> | undefined) => void,
  withLTP?: boolean,
  withCity?: boolean
) => {
  if (parkingZonesArray && parkingZonesArray?.length > 0) {
    const parkingZonesFiltered = parkingZonesArray?.filter(
      (p: ParkZone | null) =>
        Boolean(p?.address?.marker?.coordinates) && Boolean(p?.uid)
    );

    return parkingZonesFiltered?.map((p: ParkZone | null, index: number) => {
      const {
        address,
        uid,
        countryCode,
        markerType,
        name = '',
        locationType,
      } = p ?? {};
      const isStreet = locationType === 'STREET' && countryCode === 'FI';

      if (address?.marker?.coordinates && uid && !isStreet) {
        const coordinates = makePoint(address.marker.coordinates);
        const zoneCode = p?.commercialZoneView?.zoneCode;

        return (
          <AimoMarker
            key={`fragment-${uid}-${index}`}
            coordinate={coordinates}
            markerType={markerType as MarkerTypes}
            testID={`${markerType}-marker-${uid}`}
            id={uid}
            zoneCode={zoneCode as string}
            clusterer={clusterer}
            withLTP={withLTP}
            activeIndex={isIos ? index : 0} //Bug on IOS that causes maps with same coordinates to flash
            text={withCity ? getMarkerLetter(name) : undefined}
            onPress={withCity ? () => onItemSelect(uid) : undefined}
            isEvChargingOnlyZone={
              !!p?.parkingMethods?.evCharging &&
              !!p?.parkingMethods?.unmanagedParking
            }
          />
        );
      }
      return <Empty key={`fragment-${uid}-${index}`} />;
    });
  }
  return null;
};

/**
 * Renders polygon overlays on a map with fade-out animation for removed polygons
 *
 * @component
 * @param {Object} props - Component props
 * @param {PolygonItem[]} props.activePolygons - Array of polygon items to render on the map
 * @param {boolean} [props.hidden] - Optional flag to hide all polygons
 *
 * @example
 *
 * <MapPolygons
 *   activePolygons={polygonData}
 *   hidden={false}
 * />
 *
 */
export const MapPolygons = ({
  activePolygons,
  onItemSelect,
  withLTP,
}: {
  activePolygons: PolygonItem[];
  onItemSelect?: (id: string) => void;
  withLTP: boolean;
}) => {
  const active = useActiveElementStore((state) => state.activeElement);

  const memoizedPolygons = useMemo(() => {
    return activePolygons.map(({ area, uid: parkingZoneUid }, index) => {
      return (
        <AimoPolygon
          key={`polygon-${parkingZoneUid}-${index}`}
          id={parkingZoneUid}
          geoJsonMultiPolygon={area!}
          hidden={false}
          isActive={isSelectedElement(parkingZoneUid, active)}
          onPress={
            onItemSelect ? () => onItemSelect(parkingZoneUid) : undefined
          }
          withLTP={withLTP}
        />
      );
    });
  }, [activePolygons, active, onItemSelect, withLTP]);

  return <>{memoizedPolygons}</>;
};
